/**
 * English strings of the command registry, keymap and keyboard router (W1-11a), namespace 'registry'.
 * Key and modifier names used in shortcut labels, recorder refusal reasons (reserved / unreliable chords),
 * keymap JSON import errors, when-expression errors, the Guided-level "Run once / Switch to Full" prompt and the
 * focus-region commands. zh-CN lives in src/i18n/zh-CN/registry.ts (localisation package).
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('registry', {
  // ---- modifiers ----
  modCtrl: 'Ctrl',
  modAlt: 'Alt',
  modShift: 'Shift',
  modCmd: 'Cmd',
  modOption: 'Option',

  // ---- named keys ----
  keySpace: 'Space',
  keyEnter: 'Enter',
  keyEscape: 'Esc',
  keyTab: 'Tab',
  keyBackspace: 'Backspace',
  keyDelete: 'Del',
  keyInsert: 'Ins',
  keyHome: 'Home',
  keyEnd: 'End',
  keyPageUp: 'PgUp',
  keyPageDown: 'PgDn',
  keyArrowUp: 'Up',
  keyArrowDown: 'Down',
  keyArrowLeft: 'Left',
  keyArrowRight: 'Right',
  keyContextMenu: 'Menu',
  keyPause: 'Pause',
  keyPrintScreen: 'PrtSc',
  keyWheel: 'Wheel',
  keyNumpad: 'Num {key}',

  // ---- label decorations ----
  labelHold: 'Hold {label}',
  labelPhysical: '{label} (physical)',

  // ---- recorder: refused chords (browser / OS never deliver them) ----
  reservedTabsWindows: 'Your browser uses {chord} to open, close or restore tabs and windows; pick another key.',
  reservedTabSwitch: 'Your browser keeps {chord} for switching tabs; pick another key.',
  reservedHistory:
    'Your browser uses {chord} to go back, forward or home, which would leave the editor; pick another key.',
  reservedCloseWindow: '{chord} closes the window on your system; pick another key.',
  reservedFullscreen: '{chord} switches the browser to full screen; pick another key.',
  reservedDevtools: "{chord} opens the browser's developer tools; pick another key.",
  reservedPrivate: '{chord} opens a private browser window; pick another key.',
  reservedAddons: "{chord} opens the browser's add-ons or tab search; pick another key.",
  reservedBrowserTool: '{chord} is used by the browser itself; pick another key.',
  reservedScreenshot: "{chord} takes a screenshot with the browser's own tool; pick another key.",
  reservedReload: '{chord} reloads the page; pick another key.',
  reservedClearData: '{chord} opens the browser\'s "Clear browsing data" window; pick another key.',
  reservedMacApp: 'On macOS, {chord} quits the browser or closes or opens tabs and windows; pick another key.',
  reservedMacWindow: 'On macOS, {chord} hides or minimises the browser; pick another key.',
  reservedMacPrefs: "On macOS, {chord} opens the browser's settings; pick another key.",
  reservedMacCycle: 'On macOS, {chord} switches between browser windows; pick another key.',
  reservedMacHistory:
    'On macOS, {chord} goes back or forward in the browser, which would leave the editor; pick another key.',
  reservedMacAddress: "On macOS, {chord} jumps to the browser's address bar; pick another key.",
  reservedMacLogout: 'On macOS, {chord} logs you out; pick another key.',
  reservedMacTabs: 'On macOS, {chord} switches browser tabs; pick another key.',
  reservedMacOther: 'On macOS, {chord} is used by the browser or the system; pick another key.',
  reservedCtrlAlt:
    'Ctrl+Alt is AltGr on many keyboard layouts (it types characters such as @ or the euro sign); pick another key.',

  // ---- recorder: accepted with a warning ----
  unreliableMenu:
    'Some browsers open their own menu with {chord}, so it may not always reach Phiedit. Menus and the palette still work.',
  unreliableAddress:
    "{chord} can move focus to the browser's address bar in some browsers. Menus and the palette still work.",
  unreliableDownloads: '{chord} opens the downloads list in some browsers. Menus and the palette still work.',
  unreliableHistory: '{chord} opens the browsing history in some browsers. Menus and the palette still work.',

  // ---- keymap JSON import ----
  importNotJson: 'This file is not valid JSON.',
  importWrongFormat: 'This file is not a Phiedit keymap.',
  importNewerVersion: 'This keymap was saved by a newer version of Phiedit (format {version}).',
  importBadPreset: 'Unknown keyboard preset "{preset}"; kept the current one.',
  importBadAltFree: 'The Alt-free setting must be true or false; kept the current one.',
  importBadEntry: 'Entry {index} is not a key binding.',
  importBadChord: 'Entry {index}: "{chord}" is not a key or key combination Phiedit understands.',
  importUnknownCommand: 'Entry {index}: there is no command called "{command}".',
  importRefused: 'Entry {index}: {reason}',
  importBadWhen: 'Entry {index}: the condition "{when}" is not valid ({error}).',
  importNothingUsable: 'None of the shortcuts in this file can be used here, so your shortcuts were not changed.',

  // ---- keymap problems (keymap.issues(): user overrides and the built-in preset audit) ----
  issueInvalidChord: '"{chord}" is not a key or key combination Phiedit understands.',
  issueInvalidWhen: 'The shortcut {chord} for "{command}" has a condition that is not valid ({error}).',
  issueUnknownCommand: 'The shortcut {chord} runs "{command}", which does not exist in this version of Phiedit.',
  issueReserved: 'The shortcut {chord} for "{command}" is kept by the browser or the system and never reaches Phiedit.',
  issueCtrlAlt: 'The shortcut {chord} for "{command}" uses Ctrl+Alt, which is AltGr on many keyboard layouts.',
  issueAltFreeMissing: 'The shortcut {chord} for "{command}" uses Alt but has no replacement for the Alt-free keys.',
  issueMacMissing: 'The shortcut {chord} for "{command}" is taken by macOS but has no macOS replacement.',
  issueMacShiftWheel:
    'The shortcut {chord} for "{command}" relies on Shift+wheel, which scrolls sideways on a Mac, but has no macOS replacement.',
  issueBadAlternate: 'The replacement {alternate} of the shortcut {chord} for "{command}" cannot be used.',
  issueRemoveInPreset:
    'The built-in shortcut {chord} for "{command}" is marked as removed, which only your own changes may do.',
  issueUnreliable:
    'The shortcut {chord} for "{command}" is also used by some browsers, so it may not always reach Phiedit.',
  issueConflict: 'The shortcut {chord} runs both "{command}" and "{other}" in the same situation.',

  // ---- when expressions ----
  whenEmptyGroup: 'empty parentheses',
  whenUnexpectedEnd: 'the condition ends too early',
  whenUnexpected: 'unexpected "{token}"',
  whenUnclosed: 'a "(" is never closed',
  whenUnknownAtom: 'unknown condition "{atom}"',
  whenUnknownName: '"{name}" cannot be compared',
  whenBadValue: '"{value}" is not a valid value for {name} (use one of: {allowed})',
  whenUnterminated: 'a quoted value is not closed',

  // ---- Guided level: Full-level commands from the palette ----
  fullTitle: '"{command}" is a Full-level tool',
  fullBody:
    'You are using the Guided level, which keeps the editor simple. You can run this command once, or switch to the Full level to see all of its tools.',
  fullRunOnce: 'Run once',
  fullRunOnceDetail: 'Stay in Guided',
  fullSwitch: 'Switch to Full',
  fullSwitchDetail: 'Show every tool from now on (change it any time in Settings)',

  // ---- command failures ----
  commandFailed: 'Something went wrong while running "{command}".',
  closeDialogFirst: 'Close the open dialog first.',

  // ---- focus regions (registered by the shell module) ----
  focusNextRegion: 'Focus next panel',
  focusPreviousRegion: 'Focus previous panel',
});

export default catalogOf(m);
